
import { Component, OnInit, ChangeDetectionStrategy, DestroyRef, inject } from '@angular/core';
import { CdkOverlayOrigin, ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { IsActiveMatchOptions, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { UserMenu } from '../../user/user-menu';
import { MenuService } from '../menu.service';
import { UserService } from '../user.service';
import { LeftMenuButtonComponent } from '../left-menu-button/left-menu-button.component';
import { SideSubMenuComponent } from '../side-sub-menu/side-sub-menu.component';
import { NgFor } from '@angular/common';


@Component({
    selector: 'cub-left-menu',
    templateUrl: './left-menu.component.html',
    styleUrls: ['./left-menu.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgFor, LeftMenuButtonComponent, SideSubMenuComponent, OverlayModule]
})
export class LeftMenuComponent implements OnInit {

  menuList: UserMenu[] = [];
  activeMenu: UserMenu | null = null;
  activeOrigin: CdkOverlayOrigin | null = null;
  selectedMenuKey: string | null = null;
  openedByHover = false;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private preferredMenuKey: string | null = null;
  private readonly routeMatchOptions: IsActiveMatchOptions = {
    paths: 'subset',
    queryParams: 'ignored',
    fragment: 'ignored',
    matrixParams: 'ignored'
  };
  readonly positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 8 },
    { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 8 },
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -8 },
    { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom', offsetX: -8 }
  ];
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private userService: UserService,
    private menuService: MenuService,
    private router: Router
  ) {
    this.destroyRef.onDestroy(() => this.cancelPendingClose());
    // initialize menu so it loads faster
    let menu = JSON.parse(localStorage.getItem("userMenu")) as UserMenu;
    if (menu != null)
      this.menuList = menu?.subMenu[0].subMenu;
  }

  ngOnInit() {
    this.getMenu();
    this.menuService.showingLeftMenu$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(show => {
        if (!show) this.closeMenu();
      });
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.closeMenu());
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateSelectedMenu());
  }

  getMenu(): void {
    this.userService.userMenu$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((menu) => {
      if (menu != null) {
        const items = menu.subMenu[0].subMenu;
        if (this.activeMenu) {
          const active = items.find(item => this.trackMenu(0, item) === this.trackMenu(0, this.activeMenu));
          if (active) {
            this.activeMenu = active;
          } else {
            this.closeMenu();
          }
        }
        this.menuList = items;
        this.updateSelectedMenu();
      }
    });
  }

  rememberSelection(): void {
    if (this.activeMenu) {
      this.preferredMenuKey = this.trackMenu(0, this.activeMenu);
      this.updateSelectedMenu();
    }
  }

  private updateSelectedMenu(): void {
    const matches = this.menuList.filter(item =>
      item.subMenu.length > 0 && this.hasActiveLink(item)
    );
    const preferred = matches.find(item => this.trackMenu(0, item) === this.preferredMenuKey);
    this.selectedMenuKey = preferred
      ? this.trackMenu(0, preferred)
      : matches.length > 0 ? this.trackMenu(0, matches[0]) : null;
    if (!preferred) this.preferredMenuKey = null;
  }

  private hasActiveLink(item: UserMenu): boolean {
    if (!item.external && item.link && this.router.isActive(this.router.createUrlTree([item.link]), this.routeMatchOptions)) {
      return true;
    }
    return item.subMenu.some(child => this.hasActiveLink(child));
  }

  trackMenu(_index: number, item: UserMenu): string {
    return `${item.name}:${item.link}:${item.icon}`;
  }

  toggleMenu(item: UserMenu, origin: CdkOverlayOrigin): void {
    this.cancelPendingClose();
    if (this.activeMenu === item) {
      if (this.openedByHover) {
        this.openedByHover = false;
      } else {
        this.closeMenu();
      }
      return;
    }
    this.activeMenu = item;
    this.activeOrigin = origin;
    this.openedByHover = false;
  }

  hoverMenu(item: UserMenu, origin: CdkOverlayOrigin): void {
    this.cancelPendingClose();
    if (this.activeMenu !== item) {
      this.activeMenu = item;
      this.activeOrigin = origin;
      this.openedByHover = true;
    }
  }

  scheduleClose(): void {
    this.cancelPendingClose();
    if (this.activeMenu) {
      this.closeTimer = setTimeout(() => this.closeMenu(), 200);
    }
  }

  cancelPendingClose(): void {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  closeMenu(restoreFocus = false): void {
    this.cancelPendingClose();
    const trigger = this.activeOrigin?.elementRef.nativeElement as HTMLElement | undefined;
    this.activeMenu = null;
    this.activeOrigin = null;
    this.openedByHover = false;
    if (restoreFocus) trigger?.focus();
  }

  onOutsideClick(event: MouseEvent): void {
    if (!(event.target instanceof Element) || !event.target.closest('cub-left-menu-button')) {
      this.closeMenu();
    }
  }

  onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeMenu(true);
    }
  }

}
